import {
  Empty_NoSqlDoc,
  FBD_Keygen_BANNED,
  MakeFBDocType,
  NoSqlDoc,
  Z_NoSqlDoc,
} from '@rabbit/firebase/doctype';
import { OneEachOptionalPersonaLink, PersonaLink } from '../persona';
import { VRMProviderInfo, Z_VRMProviderInfo } from './external-service';
import { Address, Z_Address } from '../base/basic';
import { SRVType } from './holding-info';
import { z, schema } from '@rabbit/utils/ts';

/* -------------------------------------------------------------------------- */
/*                               Tenant subtypes                              */
/* -------------------------------------------------------------------------- */

export const TenantLinkFieldName = 'tenantLink';
export const PrincipalsFieldName = 'principals';
export type TenantLink = string;

export interface VendableValidationConfig {
  pubRequired: string[];
  privRequired: string[];
  /** Whether to skip validation entirely for this tenant */
  skipValidation?: boolean;
}

export const Z_VendableValidationConfig = z.object({
  pubRequired: z.array(z.string()),
  privRequired: z.array(z.string()),
  skipValidation: z.boolean().optional(),
});

export const Z_TenantLink = z.string();

/** The tenant's service class tier.
 * Currently just 'free' (for partner Tenants, who get their access from premium ones)
 * and 'premium' for tenants with a full-fat platform customization,
 * e.g. their own subdomain, custom flows, etc */
export enum TenantServiceClass {
  /** Free tier, limited features */
  Free = 'free',

  /** Paid tier, full features */
  Premium = 'premium',
}

export type TenantSRVConfiguration = {
  type: SRVType;
  parameters: { name: string; type: string }[];
};

export interface PartnerSettings {
  /** The premium tenant these settings apply to */
  partner: TenantLink;
  /** Labour rates for Pinnacle Warranties partners.
   * Values are in GBP/hour
   */
  labourRates?: {
    default: number | null;
    tier1?: number | null;
    tier2?: number | null;
    tier3?: number | null;
    currency: string;
    // maybe more in the future?
  };
}

export const Z_PartnerSettings_LabourRates = z.object({
  default: z.number().nullable(),
  tier1: z.number().nullable().optional(),
  tier2: z.number().nullable().optional(),
  tier3: z.number().nullable().optional(),
  currency: z.string(),
});

export const Z_PartnerSettings = z.object({
  partner: Z_TenantLink,
  labourRates: Z_PartnerSettings_LabourRates.optional(),
});
/* -------------------------------------------------------------------------- */
/*                        Main Tenant document schemas                        */
/* -------------------------------------------------------------------------- */

/* --------------------------------- Private -------------------------------- */
export interface DTTenant_Private extends NoSqlDoc {
  /** Principal Personas in the organisation, they can delegate access permissions */
  rootPersonas: OneEachOptionalPersonaLink;

  /** Identifier for the connected Shopify shop, which is an uppercased shop name. eg 'UNION-AUDIO-LIMITED-1405' */
  shopifyLink?: string;

  /** The tenant's service class tier. */
  serviceClass: TenantServiceClass;

  /** Settings specific to partner tenants */
  partnerSettings?: PartnerSettings[];

  // The VRM provider that this tenant uses
  VRMProvider?: VRMProviderInfo;

  /** Validation configuration for vendables specific to this tenant */
  vendableValidation?: VendableValidationConfig;
}

export const Z_DTTenant_Private = schema<DTTenant_Private>()(
  z
    .object({
      rootPersonas: z.record(z.string().nullable()),
      shopifyLink: z.string().optional(),
      serviceClass: z.nativeEnum(TenantServiceClass),
      partnerSettings: z.array(Z_PartnerSettings).optional(),
      VRMProvider: Z_VRMProviderInfo.optional(),
      vendableValidation: Z_VendableValidationConfig.optional(),
    })
    .merge(Z_NoSqlDoc)
);

export const FBD_Tenant_Private = MakeFBDocType<DTTenant_Private>({
  name: 'Tenant_Private',
  collection: 'tenant_private',
  empty: () => {
    const result: DTTenant_Private = {
      ...Empty_NoSqlDoc(),
      rootPersonas: {},
      serviceClass: TenantServiceClass.Free,
    };
    return result;
  },
  keygen: FBD_Keygen_BANNED(
    'docid should be set manually, and should be the same as the related public document.'
  ),
});

/* --------------------------------- Public --------------------------------- */
const Z_EmailInfoSubjects = z.object({
  claimRegistrationWaitingReview: z.string().optional(),
  itemRegistered: z.string().optional(),
  claimRegisteredWaitingReview: z.string().optional(),
  activateWarranty: z.string().optional(),
  invitedToJoin: z.string().optional(),
  stoneRegistrationThankYou: z.string().optional(),
  vehicleRegistration: z.string().optional(),
  editWarranty: z.string().optional(),
  loginLink: z.string().optional(),
});

const Z_EmailInfo = z.object({
  emailSender: z.string().optional(),
  emailMainTemplate: z.string().optional(),
  emailTemplateLanguage: z.string().optional(),
  emailFinishingMessage: z.string().optional(),
  subjects: Z_EmailInfoSubjects.optional(),
});

export const Z_TenantInfo = z.object({
  logo: z.string().optional(),
  logoInverted: z.string().optional(),
  name: z.string().optional(),
  shortName: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  address: z.array(Z_Address).optional(),
  country: z.string().optional(),
  currency: z.string().optional(),
  UPCPrefix: z.string().optional(),
  productPlaceholder: z.string().optional(),
  productSuggestions: z.string().optional(),
  warrantyInfoUrl: z.string().optional(),
  shopifyUrl: z.string().optional(),
  claimsInfoUrl: z.string().optional(),
  claimsInfoPhone: z.string().optional(),
  claimsInfoEmail: z.string().optional(),
  authStrapline: z.string().optional(),
  sageAuthStrapline: z.string().optional(),
  emailInfo: Z_EmailInfo.optional(),
  mainGroup: z.string().optional(),
  /**
   * When true, Sage should show a global "payment overdue" banner.
   * Default behavior: if missing/false, treat as not overdue (banner hidden).
   */
  payment_overdue_alert: z.boolean().optional(),
  /**
   * Optional allowlist of additional emails that should also see the banner.
   * Emails are compared case-insensitively.
   */
  payment_overdue_alert_emails: z.array(z.string()).optional(),
  brandDetails: z.record(z.any()).optional(),
  allowedCurrencies: z.array(z.string()).optional(),
  /** Optional. If omitted or empty, UI should show all supported countries. */
  allowedCountries: z.array(z.string()).optional(),
  messageOfTheMoment: z.string().optional(),
  serialNumberHelpText: z.string().optional(),
  /**
   * Access control flags to deny platform access for this tenant.
   * All flags are optional; false/undefined means allowed.
   */
  access_deny_all: z.boolean().optional(),
  access_deny_admin: z.boolean().optional(),
  access_deny_repairer: z.boolean().optional(),
  access_deny_retailer: z.boolean().optional(),
  access_deny_manufacturer: z.boolean().optional(),
  access_deny_warrantor: z.boolean().optional(),
  access_deny_emails: z.array(z.string()).optional(),
});

export interface TenantInfo {
  logo?: string;
  logoInverted?: string; // Dark mode / Inverted logo, for dark surfaces
  name?: string;
  shortName?: string;
  email?: string;
  phone?: string;
  address?: Address[];
  country?: string;
  currency?: string;
  UPCPrefix?: string;
  productPlaceholder?: string;
  productSuggestions?: string;
  warrantyInfoUrl?: string;
  shopifyUrl?: string;
  claimsInfoUrl?: string;
  claimsInfoPhone?: string;
  claimsInfoEmail?: string;
  authStrapline?: string;
  sageAuthStrapline?: string;
  emailInfo?: EmailInfo;
  /** The user's salesperson code, account number, and main group, if applicable ( only for Supagard) */
  salespersonCode?: string;
  accountNumber?: string;
  mainGroup?: string;
  /**
   * When true, Sage should show a global "payment overdue" banner.
   * Default behavior: if missing/false, treat as not overdue (banner hidden).
   */
  payment_overdue_alert?: boolean;
  /**
   * Optional allowlist of additional emails that should also see the banner.
   * Emails are compared case-insensitively.
   */
  payment_overdue_alert_emails?: string[];
  payment_deadline?: string;
  brands?: string[];
  brandDetails?: {};
  /**
   * Optional.
   * If omitted or empty, UI should fall back to showing all supported currencies.
   */
  allowedCurrencies?: string[];
  /** Optional. If omitted or empty, UI should fall back to showing all supported countries. */
  allowedCountries?: string[];
  messageOfTheMoment?: string;
  serialNumberHelpText?: string;
  /** Access control flags to deny platform access for this tenant. */
  access_deny_all?: boolean;
  access_deny_admin?: boolean;
  access_deny_repairer?: boolean;
  access_deny_retailer?: boolean;
  access_deny_manufacturer?: boolean;
  access_deny_warrantor?: boolean;
  access_deny_emails?: string[];
}

export interface EmailInfo {
  emailSender?: string;
  emailMainTemplate?: string;
  emailTemplateLanguage?: string;
  emailFinishingMessage?: string;
  subjects?: {
    claimRegistrationWaitingReview?: string;
    itemRegistered?: string;
    claimRegisteredWaitingReview?: string;
    activateWarranty?: string;
    invitedToJoin?: string;
    stoneRegistrationThankYou?: string;
    vehicleRegistration?: string;
    editWarranty?: string;
    loginLink?: string;
  };
}

// export type TenantInfo = z.infer<typeof Z_TenantInfo>;

// Only supagard has makes and models
export interface TenantMakesModels {
  docid: string;
  label: string;
  description: string;
  enabled: boolean;
}

export const Z_TenantMakesModels = z.object({
  docid: z.string(),
  label: z.string(),
  description: z.string(),
  enabled: z.boolean(),
});

export interface DTTenant_Public extends NoSqlDoc {
  // (M)ixmaster(C)onfiguration(K)eys, public information that can be updated by the tenant, it is used by mixmaster to build the lexicon
  mck?: Record<string, string>;

  principals?: PersonaLink[];

  /** The tenant's organization name. */
  orgName?: string;

  /** Other tenants that this tenant is a partner to */
  isPartnerTo?: TenantLink[];

  /** The tenant's self registered vendable configuration */
  srvConfiguration?: TenantSRVConfiguration;

  /** The tenant's information */
  info?: TenantInfo;

  /** The tenant's makes : only supagard has makes */
  makes?: TenantMakesModels[];

  /** The tenant's models : only supagard has models */
  models?: TenantMakesModels[];

  /** Email of the tenant : possibly deprecated */
  email?: string;

  /** The last time the tenant logged in */
  lastLoginTime?: number;
}

export const Z_DTTenant_Public = schema<DTTenant_Public>()(
  z.object({
    mck: z.record(z.string()).optional(),
    principals: z.array(z.string()).optional(),
    orgName: z.string().optional(),
    isPartnerTo: z.array(z.string()).optional(),
    srvConfiguration: z
      .object({
        type: z.nativeEnum(SRVType),
        parameters: z.array(
          z.object({
            name: z.string(),
            type: z.string(),
          })
        ),
      })
      .optional(),
    info: Z_TenantInfo.optional(),
    email: z.string().optional(),
    makes: z.array(Z_TenantMakesModels).optional(),
    models: z.array(Z_TenantMakesModels).optional(),
    lastLoginTime: z.number().optional(),
  })
).merge(Z_NoSqlDoc);

export const FBD_Tenant_Public = MakeFBDocType<DTTenant_Public>({
  name: 'Tenant_Public',
  collection: 'tenant_public',
  empty: () => {
    const result: DTTenant_Public = {
      ...Empty_NoSqlDoc(),
    };
    return result;
  },
  keygen: FBD_Keygen_BANNED(
    'docid should be set to that of the related private document.'
  ),
});
